import type { SheetComment } from "@/components/handyout-components/comment-sheet";
import { getModuleTypeMeta } from "@/lib/module-type-meta";
import type {
  DraggableModuleType,
  ModulesArray,
  PageModule,
} from "@/types/modules";
import { DEMO_MODULES } from "@/utils/demo-data";

export const LIVE_PHONE_WIDTH = 348;
export const LIVE_PHONE_HEIGHT = 700;

type LiveSpec =
  | DraggableModuleType
  | { type: DraggableModuleType; overrides: Record<string, unknown> };

function liveModule(scope: string, spec: LiveSpec) {
  const type = typeof spec === "string" ? spec : spec.type;
  const overrides = typeof spec === "string" ? {} : spec.overrides;
  return { ...DEMO_MODULES[type], ...overrides, id: `${scope}-${type}` };
}

function livePages(scope: string, specs: LiveSpec[]): PageModule[] {
  return specs.map((spec) => {
    const entry = liveModule(scope, spec);
    return {
      slideId: entry.id,
      title: getModuleTypeMeta(entry.type).label,
      modules: [entry],
    };
  });
}

const REVENUE_BAR: LiveSpec = {
  type: "bar",
  overrides: { title: "Revenue and costs" },
};

export const LIVE_SCAN_PAGES = livePages("live-scan", [
  "insight-panel",
  "ring-kpis",
]);

export const LIVE_READ_PAGES = livePages("live-read", [
  REVENUE_BAR,
  "breakdown",
]);

export const LIVE_READ_LEFT_PAGES = livePages("live-read-left", [
  "ring-kpis",
  "timeline-stepper",
]);

export const LIVE_READ_RIGHT_PAGES = livePages("live-read-right", [
  "stage-bars",
  "quadrant-matrix",
]);

export type LiveSlide = {
  slideId: string;
  title: string;
  pageNumber: number;
  totalPages: number;
  modules: ModulesArray;
  comments: SheetComment[];
  reactions: { up: number; down: number };
};

const SPEAKER = "Dana Weiss";

export const GROWTH_SLIDE: LiveSlide = {
  slideId: "live-growth",
  title: "Business development",
  pageNumber: 3,
  totalPages: 12,
  modules: [liveModule("live-growth", "metric-tabs-chart")],
  comments: [
    {
      id: "live-growth-1",
      author: "Mia Hoffmann",
      time: "6m",
      text: "Is the dashed line the previous year?",
      likes: 7,
      replies: [
        {
          id: "live-growth-1-1",
          author: SPEAKER,
          time: "4m",
          text: "Yes, last year for comparison.",
          isSpeaker: true,
          likes: 3,
        },
      ],
    },
    {
      id: "live-growth-2",
      author: "Sam Rivera",
      time: "3m",
      text: "The utilisation tab answered my question.",
      likes: 4,
    },
    {
      id: "live-growth-3",
      author: "Leo Park",
      time: "now",
      text: "Tapping through the months beats squinting at the slide.",
      likes: 1,
    },
  ],
  reactions: { up: 31, down: 2 },
};

export const PIPELINE_SLIDE: LiveSlide = {
  slideId: "live-pipeline",
  title: "Sales pipeline",
  pageNumber: 4,
  totalPages: 12,
  modules: [liveModule("live-pipeline", "stage-bars")],
  comments: [
    {
      id: "live-pipeline-1",
      author: "Priya Nair",
      time: "5m",
      text: "Where does the drop between offer and negotiation come from?",
      likes: 9,
      replies: [
        {
          id: "live-pipeline-1-1",
          author: SPEAKER,
          time: "3m",
          text: "Mostly pricing. The next slide breaks it down by deal size.",
          isSpeaker: true,
          likes: 5,
        },
      ],
    },
    {
      id: "live-pipeline-2",
      author: "Jonas Berg",
      time: "2m",
      text: "Are the Q2 numbers in here too?",
      likes: 2,
      replies: [
        {
          id: "live-pipeline-2-1",
          author: "Lena Hart",
          time: "1m",
          text: "Yes, switch the period to Q2 2026.",
          likes: 3,
        },
      ],
    },
    {
      id: "live-pipeline-3",
      author: "Alex Chen",
      time: "now",
      text: "Great overview, thanks.",
      likes: 0,
    },
  ],
  reactions: { up: 24, down: 2 },
};

export const REVENUE_SLIDE: LiveSlide = {
  slideId: "live-revenue",
  title: "Revenue and costs",
  pageNumber: 2,
  totalPages: 12,
  modules: [liveModule("live-revenue", REVENUE_BAR)],
  comments: [
    {
      id: "live-revenue-1",
      author: "Tom Becker",
      time: "2m",
      text: "Costs in Q3 jumped quite a bit.",
      likes: 3,
    },
    {
      id: "live-revenue-2",
      author: "Nina Vogel",
      time: "now",
      text: "Tap a bar and you get the exact value.",
      likes: 2,
    },
  ],
  reactions: { up: 42, down: 3 },
};
